package org.gymly.ui.components

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.gymly.theme.AdminColors
import kotlin.math.roundToInt

/**
 * Columna con scroll vertical y barra visible/arrastrable cuando el contenido desborda.
 * En web (sin rueda de mouse) permite bajar arrastrando la barra o haciendo click en el riel.
 */
@Composable
fun VerticalScrollColumn(
    modifier: Modifier = Modifier,
    state: ScrollState = rememberScrollState(),
    barWidth: Dp = 10.dp,
    contentPaddingEnd: Dp = 4.dp,
    verticalArrangement: Arrangement.Vertical = Arrangement.Top,
    horizontalAlignment: Alignment.Horizontal = Alignment.Start,
    content: @Composable ColumnScope.() -> Unit,
) {
    val showBar = state.maxValue > 0 || state.canScrollForward || state.canScrollBackward
    Box(modifier) {
        Column(
            Modifier
                .fillMaxSize()
                .padding(end = if (showBar) barWidth + contentPaddingEnd else 0.dp)
                .verticalScroll(state),
            verticalArrangement = verticalArrangement,
            horizontalAlignment = horizontalAlignment,
            content = content,
        )
        if (showBar) {
            GymlyVerticalScrollbar(
                state = state,
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxHeight()
                    .padding(vertical = 2.dp)
                    .width(barWidth),
            )
        }
    }
}

/**
 * Fila con scroll horizontal y barra visible/arrastrable cuando el contenido desborda.
 */
@Composable
fun HorizontalScrollRow(
    modifier: Modifier = Modifier,
    state: ScrollState = rememberScrollState(),
    barHeight: Dp = 10.dp,
    content: @Composable () -> Unit,
) {
    val showBar = state.maxValue > 0 || state.canScrollForward || state.canScrollBackward
    Column(modifier) {
        Box(
            Modifier
                .weight(1f)
                .fillMaxWidth()
                .horizontalScroll(state),
        ) {
            content()
        }
        if (showBar) {
            GymlyHorizontalScrollbar(
                state = state,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 4.dp)
                    .height(barHeight),
            )
        }
    }
}

@Composable
fun GymlyHorizontalScrollbar(
    state: ScrollState,
    modifier: Modifier = Modifier,
    trackColor: Color = AdminColors.Outline.copy(alpha = 0.45f),
    thumbColor: Color = AdminColors.Primary.copy(alpha = 0.7f),
    minThumbWidth: Dp = 32.dp,
) {
    val scope = rememberCoroutineScope()
    val density = LocalDensity.current
    BoxWithConstraints(
        modifier
            .clip(RoundedCornerShape(999.dp))
            .background(trackColor),
    ) {
        val trackWidthPx = with(density) { maxWidth.toPx() }.coerceAtLeast(1f)
        val minThumbPx = with(density) { minThumbWidth.toPx() }
        val effectiveMinThumbPx = minOf(minThumbPx, trackWidthPx)
        val maxValue = state.maxValue.coerceAtLeast(1)
        val contentPx = trackWidthPx + maxValue
        val thumbWidthPx = (trackWidthPx * (trackWidthPx / contentPx))
            .coerceIn(effectiveMinThumbPx, trackWidthPx)
        val travelPx = (trackWidthPx - thumbWidthPx).coerceAtLeast(1f)
        val thumbOffsetPx = (state.value.toFloat() / maxValue) * travelPx
        val thumbWidthDp = with(density) { thumbWidthPx.toDp() }
        val thumbOffsetDp = with(density) { thumbOffsetPx.toDp() }

        Box(
            Modifier
                .fillMaxSize()
                .pointerInput(maxValue, trackWidthPx, thumbWidthPx, travelPx) {
                    detectTapGestures { offset ->
                        val targetRatio = ((offset.x - thumbWidthPx / 2f) / travelPx).coerceIn(0f, 1f)
                        scope.launch {
                            state.scrollTo((targetRatio * maxValue).roundToInt())
                        }
                    }
                },
        )

        Box(
            Modifier
                .align(Alignment.CenterStart)
                .fillMaxHeight()
                .padding(vertical = 1.dp)
                .offset(x = thumbOffsetDp)
                .width(thumbWidthDp)
                .clip(RoundedCornerShape(999.dp))
                .background(thumbColor)
                .pointerInput(maxValue, travelPx) {
                    var dragAccum = 0f
                    detectDragGestures(
                        onDragStart = { dragAccum = 0f },
                        onDrag = { change, dragAmount ->
                            change.consume()
                            dragAccum += dragAmount.x
                            val deltaScroll = (dragAccum / travelPx) * maxValue
                            if (kotlin.math.abs(deltaScroll) >= 1f) {
                                val next = (state.value + deltaScroll).roundToInt().coerceIn(0, maxValue)
                                scope.launch { state.scrollTo(next) }
                                dragAccum -= (deltaScroll / maxValue) * travelPx
                            }
                        },
                    )
                },
        )
    }
}

@Composable
fun GymlyVerticalScrollbar(
    state: ScrollState,
    modifier: Modifier = Modifier,
    trackColor: Color = AdminColors.Outline.copy(alpha = 0.45f),
    thumbColor: Color = AdminColors.Primary.copy(alpha = 0.7f),
    minThumbHeight: Dp = 32.dp,
) {
    val scope = rememberCoroutineScope()
    val density = LocalDensity.current
    BoxWithConstraints(
        modifier
            .clip(RoundedCornerShape(999.dp))
            .background(trackColor),
    ) {
        val trackHeightPx = with(density) { maxHeight.toPx() }.coerceAtLeast(1f)
        val minThumbPx = with(density) { minThumbHeight.toPx() }
        // En el primer layout el riel puede ser más bajo que el thumb mínimo; coerceIn exige min <= max.
        val effectiveMinThumbPx = minOf(minThumbPx, trackHeightPx)
        val maxValue = state.maxValue.coerceAtLeast(1)
        val contentPx = trackHeightPx + maxValue
        val thumbHeightPx = (trackHeightPx * (trackHeightPx / contentPx))
            .coerceIn(effectiveMinThumbPx, trackHeightPx)
        val travelPx = (trackHeightPx - thumbHeightPx).coerceAtLeast(1f)
        val thumbOffsetPx = (state.value.toFloat() / maxValue) * travelPx
        val thumbHeightDp = with(density) { thumbHeightPx.toDp() }
        val thumbOffsetDp = with(density) { thumbOffsetPx.toDp() }

        Box(
            Modifier
                .fillMaxSize()
                .pointerInput(maxValue, trackHeightPx, thumbHeightPx, travelPx) {
                    detectTapGestures { offset ->
                        val targetRatio = ((offset.y - thumbHeightPx / 2f) / travelPx).coerceIn(0f, 1f)
                        scope.launch {
                            state.scrollTo((targetRatio * maxValue).roundToInt())
                        }
                    }
                },
        )

        Box(
            Modifier
                .align(Alignment.TopStart)
                .fillMaxWidth()
                .padding(horizontal = 1.dp)
                .offset(y = thumbOffsetDp)
                .height(thumbHeightDp)
                .clip(RoundedCornerShape(999.dp))
                .background(thumbColor)
                .pointerInput(maxValue, travelPx) {
                    var dragAccum = 0f
                    detectDragGestures(
                        onDragStart = { dragAccum = 0f },
                        onDrag = { change, dragAmount ->
                            change.consume()
                            dragAccum += dragAmount.y
                            val deltaScroll = (dragAccum / travelPx) * maxValue
                            if (kotlin.math.abs(deltaScroll) >= 1f) {
                                val next = (state.value + deltaScroll).roundToInt().coerceIn(0, maxValue)
                                scope.launch { state.scrollTo(next) }
                                dragAccum -= (deltaScroll / maxValue) * travelPx
                            }
                        },
                    )
                },
        )
    }
}
